import React from 'react';
import { Upload, Settings, CheckCircle, X, AlertTriangle } from 'lucide-react';

interface ThreeStepProgressProps {
  currentStep: 'upload' | 'processing' | 'done' | 'failed';
  fileName?: string;
  onClose: () => void;
  onRetry?: () => void;
  showRetryButton?: boolean;
  failureType?: 'upload' | 'processing';
}

const ThreeStepProgress: React.FC<ThreeStepProgressProps> = ({ currentStep, fileName, onClose, onRetry, showRetryButton = true, failureType = 'processing' }) => {
  const steps = [
    { id: 'upload', label: 'Upload', icon: Upload },
    { id: 'processing', label: 'Processing', icon: Settings },
    { id: 'done', label: 'Done', icon: CheckCircle },
  ];

  const getStepIndex = (step: string) => steps.findIndex(s => s.id === step);
  const currentIndex = getStepIndex(currentStep);
  const isFailed = currentStep === 'failed';
  const isDone = currentStep === 'done';
  const showCloseButton = isFailed || isDone;

  return (
    <div className={`p-3 bg-surface-800/30 rounded-lg relative ${
      isFailed ? 'border-2 border-accent-red' : 'border border-engramiq-green/20'
    }`}>
      {/* Close Button - only show in done or failed states */}
      {showCloseButton && (
        <button
          onClick={onClose}
          className={`absolute top-2 right-2 p-1 rounded transition-all ${
            isFailed
              ? 'hover:bg-accent-red/10 text-engramiq-bright-grey hover:text-accent-red'
              : 'hover:bg-engramiq-green/10 text-engramiq-bright-grey hover:text-engramiq-green'
          }`}
          title="Close"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      {fileName && (
        <h3 className="text-sm font-semibold text-engramiq-light-grey mb-3 pr-8">
          {fileName}
        </h3>
      )}

      <div className="flex items-center justify-between mb-3">
        {isFailed ? (
          // Failed state - show error icon and message
          <div className="flex flex-col items-center gap-3 flex-1">
            <div className="w-12 h-12 bg-accent-red/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-accent-red" />
            </div>
            <p className="text-sm text-white text-center leading-relaxed px-2">
              {failureType === 'upload'
                ? 'Upload unsuccessful. Please try again or contact support@engramiq.com if the problem persists.'
                : 'Processing unsuccessful. Please try again or contact support@engramiq.com if the problem persists.'}
            </p>
          </div>
        ) : (
          // Normal progress states
          steps.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = index === currentIndex;
            const isCompleted = index < currentIndex;
            const isUpcoming = index > currentIndex;

            return (
              <React.Fragment key={step.id}>
                {/* Step Circle */}
                <div className="flex flex-col items-center gap-1.5 flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isActive && step.id !== 'done'
                        ? 'bg-gradient-green-blue shadow-engramiq scale-110 animate-pulse'
                        : isActive && step.id === 'done'
                        ? 'bg-gradient-green-blue shadow-engramiq scale-110'
                        : isCompleted
                        ? 'bg-engramiq-green'
                        : 'bg-surface-800 border-2 border-engramiq-green/30'
                    }`}
                  >
                    <StepIcon
                      className={`w-5 h-5 transition-all ${
                        isActive
                          ? 'text-engramiq-dark-blue'
                          : isCompleted
                          ? 'text-engramiq-dark-blue'
                          : 'text-engramiq-bright-grey'
                      }`}
                    />
                  </div>

                  <span
                    className={`text-xs font-semibold transition-all ${
                      isActive && step.id !== 'done'
                        ? 'text-engramiq-green animate-pulse'
                        : isActive || isCompleted
                        ? 'text-engramiq-green'
                        : 'text-engramiq-bright-grey'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>

                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="flex-1 h-1 mx-2 relative" style={{ maxWidth: '80px' }}>
                    <div className="absolute inset-0 bg-surface-800 rounded-full" />
                    <div
                      className={`absolute inset-0 rounded-full transition-all duration-500 ${
                        index < currentIndex
                          ? 'bg-engramiq-green w-full'
                          : 'bg-transparent w-0'
                      }`}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })
        )}
      </div>

      {/* Informational Text or Action Buttons */}
      <div className="text-center">
        {isFailed ? (
          // Failed state - conditionally show Cancel and Retry buttons
          showRetryButton ? (
            <div className="flex gap-3 justify-center mt-2">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-transparent hover:bg-accent-red/10 border border-accent-red/40 hover:border-accent-red rounded-lg text-sm text-accent-red hover:text-accent-red transition-all"
              >
                Cancel
              </button>
              <button
                onClick={onRetry}
                className="px-4 py-2 bg-gradient-green-blue hover:brightness-110 rounded-lg text-sm text-engramiq-dark-blue hover:text-engramiq-dark-blue transition-all hover:shadow-engramiq flex items-center gap-2"
              >
                Retry
              </button>
            </div>
          ) : null
        ) : (
          <p className="text-sm text-white leading-relaxed">
            {currentStep === 'done'
              ? 'Upload complete'
              : 'Sites can take between 5 minutes - 2 hours to upload depending on their size and complexity. When a site is complete the progress bar will show as \'done\' and the site will appear in the sites list below. If the upload fails you will receive an upload failure message.'}
          </p>
        )}
      </div>
    </div>
  );
};

export default ThreeStepProgress;
